/**
 * WinnersModal — pops up when a Last-Touch round settles, showing who won and
 * how much they took home.
 *
 * <RoundWinners> is the self-contained wrapper: mount it once in the lobby with
 * the KotH controller. It
 *   - captures the pot while the round is open (end_round zeroes it on settle),
 *   - falls back to the indexer's recorded final pot for a load-into-settled,
 *   - auto-opens once per round id when status flips to Settled,
 *   - frees the pointer-lock cursor while open.
 *
 * Payout amounts come from computeWinnersBreakdown, an exact mirror of the
 * program's position-weighted split — see winnersSplit.ts.
 */
import { PublicKey } from "@solana/web3.js";
import React, { useEffect, useRef, useState } from "react";

import type { KothLobbyController } from "./useKothLobbyController";
import type { KothRoundState } from "./useKothRound";
import { shortWallet, useLastRound } from "./useLeaderboard";
import { computeWinnersBreakdown, type WinnerShare } from "./winnersSplit";

const LAMPORTS_PER_SOL = 1_000_000_000;

function fmtSol(lamports: number): string {
  const sol = lamports / LAMPORTS_PER_SOL;
  if (sol === 0) return "0";
  if (sol < 0.0001) return sol.toExponential(2);
  return sol.toFixed(4).replace(/\.?0+$/, "");
}

function positionLabel(position: number): string {
  if (position === 0) return "Winner";
  if (position === 1) return "2nd";
  if (position === 2) return "3rd";
  return `${position + 1}th`;
}

function positionMedal(position: number): string {
  if (position === 0) return "👑";
  if (position === 1) return "🥈";
  if (position === 2) return "🥉";
  return "🏅";
}

export function RoundWinners({
  controller,
}: {
  controller: KothLobbyController;
}): JSX.Element {
  const { round, settled, roundId, localWalletPubkey } = controller;

  // Capture the pot while the round is OPEN, keyed by round id. end_round sets
  // pot_lamports = 0 on settlement, so by the time we observe status=Settled the
  // live PDA reads 0 — this snapshot is the only on-chain-instant source.
  const capturedPotRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (round && round.status === 0) {
      const pot = Number(round.potLamports.toString());
      if (pot > 0) capturedPotRef.current.set(round.roundId.toString(), pot);
    }
  }, [round]);

  // Indexer fallback for the final pot when we joined after settlement (so we
  // never captured it live). Matched by round id to avoid showing a stale pot.
  const lastRound = useLastRound({ gameId: "koth" });

  const [openRoundId, setOpenRoundId] = useState<string | null>(null);
  const seenRef = useRef<Set<string>>(new Set());

  const roundKey = roundId ? roundId.toString() : null;
  useEffect(() => {
    if (!settled || !roundKey) return;
    if (seenRef.current.has(roundKey)) return;
    seenRef.current.add(roundKey);
    setOpenRoundId(roundKey);
  }, [settled, roundKey]);

  // Free the cursor while the modal is up (the lobby holds pointer lock).
  useEffect(() => {
    if (openRoundId && document.pointerLockElement) document.exitPointerLock?.();
  }, [openRoundId]);

  if (!openRoundId || !round || !settled) return <></>;

  const captured = capturedPotRef.current.get(openRoundId);
  const indexerPot =
    lastRound.data &&
    lastRound.data.roundId !== null &&
    String(lastRound.data.roundId) === openRoundId
      ? lastRound.data.finalPotLamports
      : null;
  // Prefer the indexer's exact end_round pot (a post-expiry fee sweep can grow
  // it past our last open-poll snapshot); fall back to the captured value for
  // the instant the modal pops before the indexer ingests, self-correcting to
  // the exact figure on the next render once it does.
  const finalPot = indexerPot ?? captured ?? null;

  return (
    <WinnersModalView
      round={round}
      finalPotLamports={finalPot}
      localWallet={localWalletPubkey}
      onClose={() => setOpenRoundId(null)}
    />
  );
}

function WinnersModalView({
  round,
  finalPotLamports,
  localWallet,
  onClose,
}: {
  round: KothRoundState;
  finalPotLamports: number | null;
  localWallet: PublicKey | null;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const breakdown =
    finalPotLamports !== null
      ? computeWinnersBreakdown(round, finalPotLamports)
      : null;

  // Winners list: prefer the computed (with amounts) breakdown; otherwise fall
  // back to the ring buffer so we can still show who placed while the pot syncs.
  const fallbackWinners: WinnerShare[] = (round.recentTouchers ?? [])
    .filter((p) => !p.equals(PublicKey.default))
    .slice(0, round.paramsSnapshot?.topNPayout ?? 3)
    .map((p, i) => ({ position: i, wallet: p.toBase58(), lamports: 0 }));
  const winners = breakdown?.shares ?? fallbackWinners;
  const hasAmounts = breakdown !== null;

  const localKey = localWallet?.toBase58() ?? null;
  const youWon = winners.find((w) => w.wallet === localKey) ?? null;
  const king = winners[0] ?? null;

  const roundNo = round.roundId.toString();
  const touchCount = round.touchCount;
  const empty = winners.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Round results"
      onClick={onClose}
      style={backdropStyle}
    >
      <style>{KEYFRAMES}</style>
      <div onClick={(e) => e.stopPropagation()} style={cardStyle}>
        <button onClick={onClose} style={closeBtnStyle} aria-label="Close">
          ×
        </button>

        <div style={kickerStyle}>ROUND #{roundNo} OVER</div>

        {empty ? (
          <>
            <h2 style={titleStyle}>No winners this round</h2>
            <p style={subtitleStyle}>
              Nobody touched the throne — the pot rolls on. Start the next round
              and claim the crown.
            </p>
          </>
        ) : (
          <>
            <h2 style={titleStyle}>
              {youWon ? (
                youWon.position === 0 ? (
                  <>👑 You won the crown!</>
                ) : (
                  <>🎉 You placed {positionLabel(youWon.position)}!</>
                )
              ) : (
                <>👑 {shortWallet(king?.wallet)} takes the crown</>
              )}
            </h2>
            <p style={subtitleStyle}>
              {hasAmounts
                ? "Winnings are credited on-chain — claim them from your wallet any time."
                : "Syncing payout amounts from chain…"}
            </p>

            <div style={listStyle}>
              {winners.map((w) => {
                const isYou = w.wallet === localKey;
                const isKing = w.position === 0;
                return (
                  <div
                    key={w.wallet + w.position}
                    style={{
                      ...rowStyle,
                      ...(isKing ? kingRowStyle : null),
                      ...(isYou ? youRowStyle : null),
                    }}
                  >
                    <span style={medalStyle} aria-hidden>
                      {positionMedal(w.position)}
                    </span>
                    <span style={placeStyle}>{positionLabel(w.position)}</span>
                    <span style={walletStyle}>
                      {isYou ? "You" : shortWallet(w.wallet)}
                    </span>
                    <span style={amountStyle}>
                      {hasAmounts ? `${fmtSol(w.lamports)} SOL` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={statsRowStyle}>
          <Stat
            label="Final pot"
            value={
              finalPotLamports !== null
                ? `${fmtSol(finalPotLamports)} SOL`
                : "—"
            }
          />
          <Stat label="Touches" value={String(touchCount)} />
          <Stat label="Winners" value={String(winners.length)} />
        </div>

        <button onClick={onClose} style={ctaStyle}>
          {youWon ? "Let's go" : "Close"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div style={statStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </div>
  );
}

const KEYFRAMES = `
@keyframes winners-pop { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: none; } }
`;

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(4, 3, 1, 0.74)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 16,
  zIndex: 1500,
  pointerEvents: "auto",
};

const cardStyle: React.CSSProperties = {
  position: "relative",
  width: "min(480px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "linear-gradient(180deg, #1a1408 0%, #0b0d14 65%)",
  border: "1px solid #5a4a18",
  borderRadius: 16,
  padding: "26px 26px 22px",
  color: "#e8e8ef",
  boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(244, 211, 106, 0.1)",
  animation: "winners-pop 0.24s ease-out",
};

const closeBtnStyle: React.CSSProperties = {
  position: "absolute",
  top: 14,
  right: 16,
  background: "transparent",
  border: "none",
  color: "#9aa0b4",
  fontSize: 26,
  lineHeight: 1,
  cursor: "pointer",
};

const kickerStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 2,
  color: "#f4d36a",
  textTransform: "uppercase",
};

const titleStyle: React.CSSProperties = {
  margin: "8px 0 6px",
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: -0.5,
};

const subtitleStyle: React.CSSProperties = {
  margin: "0 0 18px",
  fontSize: 13,
  color: "#9aa0b4",
  lineHeight: 1.45,
};

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  background: "rgba(255,255,255,0.02)",
  border: "1px solid #2a2a36",
  borderRadius: 10,
};

const kingRowStyle: React.CSSProperties = {
  background: "rgba(244, 211, 106, 0.1)",
  border: "1px solid #5a4a18",
};

const youRowStyle: React.CSSProperties = {
  boxShadow: "0 0 0 1px rgba(127, 230, 168, 0.5)",
};

const medalStyle: React.CSSProperties = { fontSize: 20, width: 24, textAlign: "center" };
const placeStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: "#c4ccd8",
  width: 52,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};
const walletStyle: React.CSSProperties = {
  flex: 1,
  fontSize: 14,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};
const amountStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 800,
  color: "#f4d36a",
  whiteSpace: "nowrap",
};

const statsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 18,
};

const statStyle: React.CSSProperties = {
  flex: 1,
  padding: "10px 12px",
  background: "#11121b",
  border: "1px solid #2a2a36",
  borderRadius: 10,
  textAlign: "center",
};
const statLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: "#9aa0b4",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 3,
};
const statValueStyle: React.CSSProperties = { fontSize: 15, fontWeight: 700 };

const ctaStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 20,
  padding: "13px 18px",
  background: "#caa53a",
  color: "#1a1408",
  border: "none",
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 800,
  cursor: "pointer",
};
